import React, { useState, useEffect, useRef } from "react";
import "./Gallery.scss";

const Gallery = () => {
    const [galleryItems, setGalleryItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('All');
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const modalRef = useRef(null);
    const galleryRef = useRef(null);

    const filters = ['All', ...categories.map(c => c.name)];

    useEffect(() => {
        fetchAll();
    }, []);

    useEffect(() => {
        filterItems();
    }, [galleryItems, selectedFilter]);

    const fetchAll = async () => {
        try {
            const [itemsRes, catsRes] = await Promise.all([
                fetch('/api/gallery/'),
                fetch('/api/gallery-categories/'),
            ]);
            if (!itemsRes.ok) throw new Error('Failed to fetch gallery items');
            const itemsData = await itemsRes.json();
            setGalleryItems(Array.isArray(itemsData) ? itemsData : (itemsData.results || []));
            if (catsRes.ok) {
                const catsData = await catsRes.json();
                setCategories(Array.isArray(catsData) ? catsData : (catsData.results || []));
            }
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const filterItems = () => {
        if (selectedFilter === 'All') {
            setFilteredItems(galleryItems);
        } else {
            setFilteredItems(galleryItems.filter(item => item.category === selectedFilter));
        }
    };

    const openModal = (item) => {
        const index = filteredItems.findIndex(img => img.id === item.id);
        setCurrentImageIndex(index);
        setSelectedImage(item);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setSelectedImage(null);
        document.body.style.overflow = 'unset';
    };

    const navigateImage = (direction) => {
        if (direction === 'next') {
            const nextIndex = (currentImageIndex + 1) % filteredItems.length;
            setCurrentImageIndex(nextIndex);
            setSelectedImage(filteredItems[nextIndex]);
        } else {
            const prevIndex = currentImageIndex === 0 ? filteredItems.length - 1 : currentImageIndex - 1;
            setCurrentImageIndex(prevIndex);
            setSelectedImage(filteredItems[prevIndex]);
        }
    };

    const handleKeyDown = (e) => {
        if (!selectedImage) return;
        
        switch(e.key) {
            case 'Escape':
                closeModal();
                break;
            case 'ArrowLeft':
                navigateImage('prev');
                break;
            case 'ArrowRight':
                navigateImage('next');
                break;
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, currentImageIndex, filteredItems]);

    if (loading) {
        return (
            <div className="gallery-page">
                <div className="gallery-hero">
                    <h1 className="hero-title">Gallery</h1>
                    <p className="hero-subtitle">Moments of fellowship, service, and prayer — celebrating our community.</p>
                </div>
                <div className="loading-skeleton">
                    <div className="skeleton-grid">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="skeleton-card"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="gallery-page">
                <div className="gallery-hero">
                    <h1 className="hero-title">Gallery</h1>
                    <p className="hero-subtitle">Moments of fellowship, service, and prayer — celebrating our community.</p>
                </div>
                <div className="error-state">
                    <p>Error: {error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="gallery-page">
            {/* Skip link for accessibility */}
            <a href="#gallery-main" className="skip-link">Skip to gallery</a>
            
            {/* Hero Section */}
            <div className="gallery-hero">
                <h1 className="hero-title">Gallery</h1>
                <p className="hero-subtitle">Moments of fellowship, service, and prayer — celebrating our community.</p>
            </div>

            {/* Filter Controls */}
            <div className="filter-controls">
                <div className="filter-pills" role="group" aria-label="Gallery filters">
                    {filters.map((filter) => (
                        <button
                            key={filter}
                            className={`filter-btn ${selectedFilter === filter ? 'active' : ''}`}
                            onClick={() => setSelectedFilter(filter)}
                            aria-pressed={selectedFilter === filter}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Gallery */}
            <main id="gallery-main" className="gallery-main" ref={galleryRef}>
                {filteredItems.length === 0 ? (
                    <div className="empty-state">
                        <p>No photos found. Try another filter or upload a photo to share a memory.</p>
                    </div>
                ) : (
                    <div className="gallery-grid" role="list">
                        {filteredItems.map((item, index) => (
                            <div 
                                key={item.id} 
                                className="gallery-item"
                                role="listitem"
                                tabIndex={0}
                                onClick={() => openModal(item)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openModal(item);
                                    }
                                }}
                                aria-label={`View image: ${item.title || 'Gallery image'}`}
                            >
                                <div className="image-container">
                                    <img 
                                        src={item.images} 
                                        alt={item.title || 'Gallery image'} 
                                        loading="lazy"
                                    />
                                    <div className="image-overlay">
                                        <div className="image-info">
                                            <h3 className="image-title">{item.title}</h3>
                                            {item.date && (
                                                <span className="image-date">{item.date}</span>
                                            )}
                                            {item.category && (
                                                <span className="image-tag">{item.category}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {/* Lightbox Modal */}
            {selectedImage && (
                <div 
                    className="lightbox-modal" 
                    ref={modalRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="lightbox-title"
                    onClick={closeModal}
                >
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="lightbox-close" 
                            onClick={closeModal}
                            aria-label="Close lightbox"
                        >
                            ×
                        </button>
                        
                        {filteredItems.length > 1 && (
                            <>
                                <button 
                                    className="lightbox-nav lightbox-prev" 
                                    onClick={() => navigateImage('prev')}
                                    aria-label="Previous image"
                                >
                                    ‹
                                </button>
                                <button 
                                    className="lightbox-nav lightbox-next" 
                                    onClick={() => navigateImage('next')}
                                    aria-label="Next image"
                                >
                                    ›
                                </button>
                            </>
                        )}
                        
                        <div className="lightbox-image-container">
                            <img 
                                src={selectedImage.images} 
                                alt={selectedImage.title || 'Gallery image'}
                                id="lightbox-image"
                            />
                        </div>
                        
                        <div className="lightbox-info">
                            <h2 id="lightbox-title">{selectedImage.title}</h2>
                            {selectedImage.description && (
                                <p className="lightbox-description">{selectedImage.description}</p>
                            )}
                            {selectedImage.date && (
                                <span className="lightbox-date">{selectedImage.date}</span>
                            )}
                        </div>
                        
                        <div className="lightbox-counter">
                            {currentImageIndex + 1} of {filteredItems.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;

